import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  xrvaGetHealth,
  xrvaListStreams,
  xrvaCreateStream,
  xrvaDeleteStream,
  xrvaAvStatus,
  xrvaStartVideo,
  xrvaStartVideoAudio,
  xrvaStopVideo,
  xrvaStartAudio,
  xrvaStopAudio,
  xrvaKillAll,
  playingVideoInfo,
} from "../../api/xrvaApi";
import "./styles/xrvaStyles.css";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { useAuth } from "../../auth/AuthContext";

export default function XRVAApp() {
  const { logout } = useAuth();

  // ---------- XRVA state ----------
  const [loading, setLoading] = useState(false);

  const [xrvaLogLines, setXrvaLogLines] = useState([]);
  const [xrvaStreamId, setXrvaStreamId] = useState("");
  const [xrvaStatus, setXrvaStatus] = useState({});
  const [xrvaAudioDev, setXrvaAudioDev] = useState("");
  const [isVideoLive, setIsVideoLive] = useState(true);
  const [isAudioLive, setIsAudioLive] = useState(true);

  const [videoAndAudioPlayout, setVideoAndAudioPlayout] = useState("none");

  const [autoPauseOnHidden, setAutoPauseOnHidden] = useState(true);

  const [xrvaShowStatus, setXrvaShowStatus] = useState(true);
  const [xrvaShowLog, setXrvaShowLog] = useState(true);

  const [videoMessage, setVideoMessage] = useState("");
  const [audioMessage, setAudioMessage] = useState("");

  // zoom/pan effects ----------
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panAtDown = useRef({ x: 0, y: 0 });
  const zoomWrapRef = useRef(null);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await zoomWrapRef.current?.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const EPS = 1e-6;

  const ZMIN = 0.5,
    ZMAX = 4,
    ZSTEP = 0.05;

  const zoomIn = () =>
    setZoom((z) => clamp(+(z + ZSTEP).toFixed(2), ZMIN, ZMAX));
  const zoomOut = () =>
    setZoom((z) => clamp(+(z - ZSTEP).toFixed(2), ZMIN, ZMAX));
  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Wheel zoom (hold Ctrl to avoid accidental scroll)
  const onWheel = (e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    const next = clamp(+(zoom + dir * 0.1).toFixed(2), ZMIN, ZMAX);
    setZoom(next);
  };

  // Drag to pan (only when zoomed)
  const onPointerDown = (e) => {
    if (zoom === 1) return;
    setPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY };
    panAtDown.current = { ...pan };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!panning) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setPan({ x: panAtDown.current.x + dx, y: panAtDown.current.y + dy });
  };
  const onPointerUp = (e) => {
    setPanning(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
  };

  // zoom/pan effects ----------

  useEffect(() => {
    if (!videoMessage) return;
    const timer = setTimeout(() => setVideoMessage(""), 10000);
    return () => clearTimeout(timer);
  }, [videoMessage]);

  useEffect(() => {
    if (!audioMessage) return;
    const timer = setTimeout(() => setAudioMessage(""), 10000);
    return () => clearTimeout(timer);
  }, [audioMessage]);

  const checkIfSomethingRunning = async () => {
    try {
      const resp = await xrvaAvStatus();

      if (!resp.video) {
        setVideoMessage("No video stream is currently running.");
        setVideoAndAudioPlayout("none");
      } else {
        const { data } = await playingVideoInfo();

        const videoAndAudio = data?.STARTED_VIDEO?.videoAndAudio ?? false;
        setVideoAndAudioPlayout(videoAndAudio ? "videoAndAudio" : "videoOnly");
        setVideoMessage(
          `A video ${
            videoAndAudio ? "and audio" : "only"
          } stream is currently running. Visit System-Information for details.`
        );
      }

      if (!resp.audio) {
        setAudioMessage("No audio stream is currently running.");
      } else {
        setAudioMessage(
          "An audio stream is currently running. Visit System-Information for details."
        );
      }
      const data = await xrvaListStreams();
      if (data.items?.length) setXrvaStreamId(data.items[0].id);
    } catch (error) {
      console.log("Error checking AV status:", error);
      // Try to extract response data if available
      const errData = error?.response?.data || error;

      if (
        errData?.error === "Unauthorized" ||
        error.message?.includes("Unauthorized")
      ) {
        logout();
      }
    }
  };
  useEffect(() => {
    checkIfSomethingRunning();
  }, []);

  const xrvaVideoElRef = useRef(null);
  const xrvaAudioElRef = useRef(null);
  const xrvaHlsVideoRef = useRef(null);
  const xrvaHlsAudioRef = useRef(null);

  // ---------- helpers ----------
  const xrvaNow = () => new Date().toLocaleTimeString();
  const xrvaLog = useCallback((msg) => {
    setXrvaLogLines((prev) => [...prev, `[${xrvaNow()}] ${msg}`]);
  }, []);

  const xrvaSafePlay = (el) => el?.play().catch(() => {});

  const xrvaToM3U8 = (u) => {
    if (!u) return null;
    if (u.endsWith(".m3u8")) return u;
    try {
      const url = new URL(u);
      if (!url.pathname.endsWith("/")) url.pathname += "/";
      url.pathname += "index.m3u8";
      return url.toString();
    } catch {
      return u.replace(/\/?$/, "/index.m3u8");
    }
  };

  const xrvaHlsUrlOf = (resp) =>
    resp?.hls ||
    resp?.hlsManifest ||
    resp?.urls?.hls ||
    resp?.urls?.hlsManifest ||
    resp?.urls?.page ||
    null;

  const xrvaPrimeHls = async (maybePageOrM3u8) => {
    const m3u8 = xrvaToM3U8(maybePageOrM3u8);
    if (!m3u8) return null;

    try {
      const page = m3u8.replace(/\/index\.m3u8$/, "/");
      await fetch(page, { cache: "no-store" });
    } catch {}

    const tryGet = async (url) => {
      try {
        const r = await fetch(url, {
          method: "GET",
          cache: "no-store",
        });
        return r.ok;
      } catch {
        return false;
      }
    };

    for (let i = 0; i < 25; i++) {
      if (await tryGet(m3u8)) return m3u8;
      const ll = m3u8.replace("/index.m3u8", "/llhls.m3u8");
      if (ll !== m3u8 && (await tryGet(ll))) return ll;
      await new Promise((r) => setTimeout(r, 200));
    }
    return null;
  };

  const xrvaAttachHls = async (mediaEl, url, hlsRef, videoAndAudio = false) => {
    if (!mediaEl || !url) return;

    // Clean up any previous hls on this element
    try {
      if (hlsRef.current?.destroy) hlsRef.current.destroy();
    } catch {}
    hlsRef.current = null;

    // Autoplay-friendly defaults
    mediaEl.playsInline = true;
    if (!videoAndAudio && mediaEl.tagName === "VIDEO") mediaEl.muted = true; // reliable autoplay
    mediaEl.preload = mediaEl.tagName === "AUDIO" ? "none" : "metadata";

    // Prefer dynamic import of hls.js. Fallback to native HLS (Safari).
    let HlsCtor = null;
    try {
      const mod = await import(/* webpackChunkName: "hlsjs" */ "hls.js");
      HlsCtor = mod?.default || null;
    } catch {
      HlsCtor = null;
    }

    // Helper: jump to live edge - not ideal to call too often
    const seekLive = () => {
      // try {
      //   const s = mediaEl.seekable;
      //   if (s && s.length) mediaEl.currentTime = s.end(s.length - 1);
      // } catch {}
    };

    if (HlsCtor && HlsCtor.isSupported()) {
      const isVideo = mediaEl.tagName === "VIDEO";

      const h = new HlsCtor({
        lowLatencyMode: true,
        startPosition: -1, // start at live edge
        // keep memory tiny (crucial with 2 players)
        maxBufferLength: 3,
        maxMaxBufferLength: 5,
        backBufferLength: 0,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        maxFragLookUpTolerance: 0.2,
        enableWorker: true,
        capLevelToPlayerSize: isVideo, // avoid 1080p on small video
        // safer defaults for flakey networks
        fragLoadingMaxRetry: 2,
        fragLoadingRetryDelay: 500,
      });

      // Error handling & recovery
      h.on(HlsCtor.Events.ERROR, (_e, data) => {
        const detail = data?.details || "unknown";
        // You can log externally if you want:
        // xrvaLog?.(`hls.js error: ${detail}`);

        if (!data?.fatal) {
          // Typical non-fatal stall → nudge to live edge
          if (detail === HlsCtor.ErrorDetails.BUFFER_STALLED_ERROR) seekLive();
          return;
        }

        switch (data.type) {
          case HlsCtor.ErrorTypes.NETWORK_ERROR:
            h.startLoad();
            break;

          case HlsCtor.ErrorTypes.MEDIA_ERROR:
            try {
              // Trim pressure, jump to live
              mediaEl.pause();
              if (mediaEl.buffered?.length) {
                const end = mediaEl.buffered.end(mediaEl.buffered.length - 1);
                mediaEl.currentTime = end;
              } else {
                seekLive();
              }
            } catch {}
            h.recoverMediaError();
            mediaEl.play?.().catch(() => {});
            break;

          default:
            h.destroy();
        }
      });

      // Attach & play
      h.attachMedia(mediaEl);
      h.on(HlsCtor.Events.MEDIA_ATTACHED, () => h.loadSource(url));
      h.on(HlsCtor.Events.MANIFEST_PARSED, () => {
        // Start at live & play
        seekLive();
        mediaEl.play?.().catch(() => {});
      });

      // Nudge if the element reports waiting
      const onWaiting = () => {
        if (mediaEl.readyState < 3) seekLive();
        mediaEl.play?.().catch(() => {});
      };
      mediaEl.addEventListener("waiting", onWaiting);

      // Pause/resume loading when tab visibility changes (frees MSE memory)
      const onVis = () => {
        if (!autoPauseOnHidden) return;
        document.hidden ? h.stopLoad() : h.startLoad();
      };

      document.addEventListener("visibilitychange", onVis);

      // Clean listeners when hls is destroyed
      h.on(HlsCtor.Events.DESTROYING, () => {
        document.removeEventListener("visibilitychange", onVis);
        mediaEl.removeEventListener("waiting", onWaiting);
      });

      hlsRef.current = h;
    } else {
      // Native HLS path (Safari, iOS)
      mediaEl.src = url;
      // Start at live after metadata
      const onLoaded = () => {
        try {
          seekLive();
        } catch {}
        mediaEl.play?.().catch(() => {});
      };
      mediaEl.addEventListener("loadedmetadata", onLoaded, { once: true });
      // Fallback play attempt
      mediaEl.play?.().catch(() => {});
      hlsRef.current = null; // no hls instance
    }
  };

  // NEW: AV-only attach (video has audio inside)
  const xrvaAttachHlsAV = async (mediaEl, url, hlsRef) => {
    if (!mediaEl || !url) return;

    // cleanup any previous
    try {
      hlsRef.current?.destroy?.();
    } catch {}
    hlsRef.current = null;

    // user-gesture prime: start muted so play() succeeds, we’ll unmute later
    mediaEl.playsInline = true;
    mediaEl.autoplay = true;
    mediaEl.muted = true; // 🔸 temporary, will be removed on "playing"
    mediaEl.preload = "metadata";
    mediaEl.crossOrigin = "anonymous";

    // preload gesture (no await to keep gesture chain intact)
    mediaEl.play?.().catch(() => {});

    // dynamic import hls.js
    let HlsCtor = null;
    try {
      const mod = await import(/* webpackChunkName:"hlsjs" */ "hls.js");
      HlsCtor = mod?.default || null;
    } catch {}

    const unmuteOnFirstPlay = () => {
      // unmute ASAP once frames flow
      mediaEl.muted = false;
      mediaEl.removeEventListener("playing", unmuteOnFirstPlay);
    };
    mediaEl.addEventListener("playing", unmuteOnFirstPlay);

    if (HlsCtor && HlsCtor.isSupported()) {
      const h = new HlsCtor({
        lowLatencyMode: false, // classic HLS is steadier for RTSP->HLS
        startPosition: -1, // live edge
        enableWorker: true,
        capLevelToPlayerSize: true,
        maxBufferLength: 10,
        maxMaxBufferLength: 20,
        backBufferLength: 30,
        liveSyncDuration: 3,
        fragLoadingMaxRetry: 2,
        fragLoadingRetryDelay: 500,
      });

      h.on(HlsCtor.Events.ERROR, (_e, data) => {
        if (!data?.fatal) return;
        if (data.type === HlsCtor.ErrorTypes.NETWORK_ERROR)
          return h.startLoad(-1);
        if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR) {
          h.recoverMediaError();
          return;
        }
        h.destroy();
      });

      h.attachMedia(mediaEl);
      h.on(HlsCtor.Events.MEDIA_ATTACHED, () => {
        h.loadSource(url); // classic /index.m3u8 (not llhls)
        h.startLoad(-1); // live edge
      });

      h.on(HlsCtor.Events.MANIFEST_PARSED, () => {
        mediaEl.play?.().catch(() => {});
      });

      hlsRef.current = h;
    } else {
      // native HLS (Safari)
      mediaEl.src = url;
      mediaEl.addEventListener(
        "loadedmetadata",
        () => {
          mediaEl.play?.().catch(() => {});
        },
        { once: true }
      );
      hlsRef.current = null;
    }
  };

  const xrvaDetachHls = (mediaEl, hlsRef) => {
    try {
      if (hlsRef.current?.destroy) hlsRef.current.destroy();
      hlsRef.current = null;
      if (mediaEl) {
        mediaEl.pause();
        mediaEl.removeAttribute("src");
        mediaEl.load();
      }
    } catch {}
  };

  // ---------- Actions ----------
  const xrvaCheckHealth = async () => {
    try {
      const d = await xrvaGetHealth();
      xrvaLog("Health: " + JSON.stringify(d));
    } catch (e) {
      alert("Failed to check health: " + e.message);
      xrvaLog("Health error: " + e.message);
    }
  };

  const xrvaFetchStreams = async () => {
    try {
      const data = await xrvaListStreams();
      xrvaLog("Streams: " + JSON.stringify(data));
      if (data.items?.length) setXrvaStreamId(data.items[0].id);
    } catch (e) {
      alert("Failed to list streams: " + e.message);
      xrvaLog("List error: " + e.message);
    }
  };

  const xrvaAddStream = async () => {
    try {
      setLoading(true);
      const data = await xrvaCreateStream({ name: "XRVA Test Stream" });
      setXrvaStreamId(data.id);
      xrvaLog("Created stream id=" + data.id);
    } catch (e) {
      alert("Failed to create stream: " + e.message);
      xrvaLog("Create error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const xrvaDelete = async () => {
    try {
      setLoading(true);
      if (!xrvaStreamId) return;
      await xrvaDeleteStream(xrvaStreamId);
      xrvaLog("Deleted stream id=" + xrvaStreamId);
      setXrvaStreamId("");
    } catch (e) {
      alert("Failed to delete stream: " + e.message);
      xrvaLog("Delete error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const xrvaOpenWebRTC = async () => {
    try {
      const { items } = await xrvaListStreams();
      const s = items?.[0];
      if (!s) {
        xrvaLog("No streams yet");
        alert("No streams available. Please create one first.");
        return;
      }

      window.open(s.urls.webrtc, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert("Failed to open WebRTC: " + e.message);
      xrvaLog("Open WebRTC error: " + e.message);
    }
  };

  const xrvaRefreshStatus = async () => {
    try {
      const s = await xrvaAvStatus();
      setXrvaStatus(s);
      xrvaLog("Status: " + JSON.stringify(s));
    } catch (e) {
      alert("Failed to refresh status: " + e.message);
      xrvaLog("Status error: " + e.message);
    }
  };

  const xrvaVideoAudioOn = async () => {
    try {
      setLoading(true);

      // Ensure audio <audio> is not competing in AV mode
      xrvaDetachHls(xrvaAudioElRef.current, xrvaHlsAudioRef);
      const resp = await xrvaStartVideoAudio();

      setVideoMessage("");
      const raw = xrvaHlsUrlOf(resp);
      if (!raw) throw new Error("No video URL from server");
      // if (xrvaVideoElRef.current) xrvaVideoElRef.current.muted = true;
      const url = await xrvaToM3U8(raw);
      if (!url) throw new Error("Video HLS manifest not ready");
      await xrvaAttachHlsAV(xrvaVideoElRef.current, url, xrvaHlsVideoRef);
      xrvaLog("Video ON → " + url);
      setVideoAndAudioPlayout("videoAndAudio");
      setVideoMessage(
        `${
          resp.reused
            ? `Stream already started by ${
                resp.startedBy.fullName || "someone else"
              }.`
            : "Stream started by you."
        } For more info, visit the System-Information page.`
      );
      xrvaRefreshStatus();
    } catch (e) {
      setVideoMessage("");
      alert("Failed to start video: " + e.message + ". Please try again.");
      xrvaLog("Video ON error: " + e.message);
    } finally {
      setLoading(false);
    }
  };
  const xrvaVideoOn = async () => {
    try {
      setLoading(true);
      const resp = await xrvaStartVideo();
      setVideoMessage("");
      const raw = xrvaHlsUrlOf(resp);
      if (!raw) throw new Error("No video URL from server");
      if (xrvaVideoElRef.current) xrvaVideoElRef.current.muted = true;
      const url = await xrvaPrimeHls(raw);
      if (!url) throw new Error("Video HLS manifest not ready");
      await xrvaAttachHls(xrvaVideoElRef.current, url, xrvaHlsVideoRef);
      xrvaLog("Video ON → " + url);
      setVideoAndAudioPlayout("videoOnly");
      setVideoMessage(
        `${
          resp.reused
            ? `Stream already started by ${
                resp.startedBy.fullName || "someone else"
              }.`
            : "Stream started by you."
        } For more info, visit the System-Information page.`
      );
      xrvaRefreshStatus();
    } catch (e) {
      setVideoMessage("");
      alert("Failed to start video: " + e.message + ". Please try again.");
      xrvaLog("Video ON error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const xrvaVideoOff = async () => {
    try {
      await xrvaStopVideo();
      xrvaDetachHls(xrvaVideoElRef.current, xrvaHlsVideoRef);
      xrvaLog("Video OFF");
      setVideoMessage("Video OFF");
      setVideoAndAudioPlayout("none");
      xrvaRefreshStatus();
    } catch (e) {
      setVideoMessage("");
      alert("Failed to stop video: " + e.message);
      xrvaLog("Video OFF error: " + e.message);
    }
  };

  const xrvaAudioOn = async () => {
    try {
      setLoading(true);
      const body = xrvaAudioDev ? { audioDev: xrvaAudioDev } : {};
      const resp = await xrvaStartAudio(body);
      const raw = xrvaHlsUrlOf(resp);

      if (!raw) throw new Error("No audio URL from server");
      const url = await xrvaPrimeHls(raw);
      if (!url) throw new Error("Audio HLS manifest not ready");
      await xrvaAttachHls(xrvaAudioElRef.current, url, xrvaHlsAudioRef);
      if (xrvaAudioElRef.current) xrvaAudioElRef.current.volume = 1;
      xrvaLog("Audio ON → " + url);
      setAudioMessage(
        `${
          resp.reused
            ? `Audio already started by ${
                resp.startedBy.fullName || "someone else"
              }.`
            : "Audio started by you."
        } For more info, visit the System-Information page.`
      );
      xrvaRefreshStatus();
    } catch (e) {
      setAudioMessage("");
      alert("Failed to start audio: " + e.message + ". Please try again.");
      xrvaLog("Audio ON error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const xrvaAudioOff = async () => {
    try {
      await xrvaStopAudio();
      xrvaDetachHls(xrvaAudioElRef.current, xrvaHlsAudioRef);
      xrvaLog("Audio OFF");
      setAudioMessage("Audio OFF");
      xrvaRefreshStatus();
    } catch (e) {
      setAudioMessage("");
      alert("Failed to stop audio: " + e.message);
      xrvaLog("Audio OFF error: " + e.message);
    }
  };

  const xrvaKillAllProc = async () => {
    setVideoMessage("");
    setAudioMessage("");
    setVideoAndAudioPlayout("none");

    try {
      xrvaAudioOff();
      xrvaVideoOff();
      await xrvaKillAll();
      setXrvaStreamId("");
      xrvaLog("Killed all processes");
      alert("All processes killed.");

      xrvaRefreshStatus();
    } catch (e) {
      alert("Failed to kill all processes: " + e.message);
      xrvaLog("Kill all error: " + e.message);
    }
  };

  // derived formatted blocks
  const xrvaStatusBlock = useMemo(
    () => `Status
${JSON.stringify(xrvaStatus, null, 2)}`,
    [xrvaStatus]
  );
  const xrvaLogBlock = useMemo(
    () => `Log
${xrvaLogLines.join("")}`,
    [xrvaLogLines]
  );

  // Cleanup HLS instances on unmount
  useEffect(
    () => () => {
      xrvaDetachHls(xrvaVideoElRef.current, xrvaHlsVideoRef);
      xrvaDetachHls(xrvaAudioElRef.current, xrvaHlsAudioRef);
    },
    []
  );

  const xrvaSeekLive = (mediaEl, setLive) => {
    if (mediaEl?.seekable?.length) {
      mediaEl.currentTime = mediaEl.seekable.end(0);
      setLive(true);
    }
  };

  useEffect(() => {
    const v = xrvaVideoElRef.current;
    if (!v) return;
    const t = setInterval(() => {
      if (v.seekable.length) {
        const diff = v.seekable.end(0) - v.currentTime;
        setIsVideoLive(diff < 2);
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const a = xrvaAudioElRef.current;
    if (!a) return;
    const t = setInterval(() => {
      if (a.seekable.length) {
        const diff = a.seekable.end(0) - a.currentTime;
        setIsAudioLive(diff < 2);
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // ---------- UI ----------
  return (
    <div className="xrva-shell">
      <header className="xrva-header">
        <div className="xrva-brand">
          <span className="xrva-dot" aria-hidden />
          <h1 className="xrva-title">Remote AV Control</h1>
        </div>
        {loading && (
          <div className="xrva-loading">
            <LoadingSpinner />
          </div>
        )}
        <div className="xrva-actions">
          <button className="xrva-btn" onClick={xrvaCheckHealth}>
            Check API
          </button>
          <button className="xrva-btn" onClick={xrvaFetchStreams}>
            List Streams
          </button>
          <button className="xrva-btn" onClick={xrvaAddStream}>
            Create Stream
          </button>
          <button className="xrva-btn" onClick={xrvaOpenWebRTC}>
            Open WebRTC
          </button>
          <button className="xrva-btn" onClick={xrvaRefreshStatus}>
            Refresh Status
          </button>
          <button
            className="xrva-btn xrva-btn--danger"
            onClick={xrvaDelete}
            disabled={!xrvaStreamId}
          >
            Delete Stream
          </button>
          <button
            className="xrva-btn xrva-btn--warning"
            onClick={xrvaKillAllProc}
          >
            Kill All Processes
          </button>
        </div>
      </header>

      <button
        className={`xrva-btn-ED-pause ${
          autoPauseOnHidden
            ? "xrva-btn-ED-pause-enabled"
            : "xrva-btn-ED-pause-disabled"
        }`}
        onClick={() => setAutoPauseOnHidden((v) => !v)}
      >
        {autoPauseOnHidden
          ? "ENABLED - Click to disable Auto Pause"
          : "DISABLED - Click to enable Auto Pause"}
      </button>

      <main className="xrva-main xrva-main--stack">
        {/* VIDEO TOP */}
        <section className="xrva-card">
          {videoMessage && <p className="requestsMessage">{videoMessage}</p>}

          <div className="xrva-card__head">
            <h2 className="xrva-card__title">Video</h2>
            <div className="xrva-inline">
              <button
                className="xrva-btn xrva-btn--primary"
                onClick={xrvaVideoAudioOn}
                disabled={videoAndAudioPlayout === "videoOnly"}
              >
                Video and Audio ON
              </button>

              <button
                className="xrva-btn xrva-btn--primary"
                onClick={xrvaVideoOn}
                disabled={videoAndAudioPlayout === "videoAndAudio"}
              >
                Video ON
              </button>
              <button className="xrva-btn" onClick={xrvaVideoOff}>
                Video OFF
              </button>
            </div>
          </div>
          <div className="xrva-media-wrap">
            <div className="xrva-inline">
              <div className="xrva-inline-controls">
                <button
                  className="xrva-btn"
                  onClick={zoomOut}
                  disabled={zoom - ZMIN <= EPS}
                >
                  ZOOM −
                </button>
                <button
                  className="xrva-btn"
                  onClick={zoomIn}
                  disabled={ZMAX - zoom <= EPS}
                >
                  ZOOM +
                </button>
                <button
                  className="xrva-btn"
                  onClick={resetZoom}
                  disabled={zoom === 1}
                >
                  Reset
                </button>
                <button
                  className="xrva-btn"
                  onClick={() => setPan({ x: 0, y: 0 })}
                  disabled={pan.x === 0 && pan.y === 0}
                >
                  Center
                </button>
                <button className="xrva-btn" onClick={toggleFullscreen}>
                  Fullscreen
                </button>
              </div>
            </div>

            <div
              ref={zoomWrapRef}
              className={`xrva-zoom-wrap ${zoom > 1 ? "is-zoomed" : ""} ${
                panning ? "is-panning" : ""
              }`}
              onWheel={onWheel}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              <div
                className="xrva-zoom-stage"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                }}
              >
                <video
                  ref={xrvaVideoElRef}
                  playsInline
                  controls
                  className="xrva-video"
                  aria-label="Remote video stream"
                />
              </div>
            </div>
          </div>
          <br />
          <div className="xrva-inline">
            <button
              className={`xrva-btn ${isVideoLive ? "xrva-btn--primary" : ""}`}
              onClick={() =>
                xrvaSeekLive(xrvaVideoElRef.current, setIsVideoLive)
              }
            >
              Go to LIVE
            </button>
          </div>
        </section>

        {/* AUDIO BELOW VIDEO */}
        <section className="xrva-card">
          {audioMessage && <p className="requestsMessage">{audioMessage}</p>}
          <div className="xrva-card__head">
            <h2 className="xrva-card__title">Audio</h2>
            <div className="xrva-inline">
              <button
                className="xrva-btn xrva-btn--primary"
                onClick={xrvaAudioOn}
              >
                Audio ON
              </button>
              <button className="xrva-btn" onClick={xrvaAudioOff}>
                Audio OFF
              </button>
            </div>
          </div>
          <div className="xrva-field-row">
            <label className="xrva-field">
              <span className="xrva-label">Audio device (optional)</span>
              <input
                className="xrva-input"
                placeholder='e.g. "plughw:0,0" or "pulse:default"'
                value={xrvaAudioDev}
                onChange={(e) => setXrvaAudioDev(e.target.value)}
              />
            </label>
          </div>
          <audio
            ref={xrvaAudioElRef}
            controls
            className="xrva-audio"
            aria-label="Remote audio stream"
          />
          <br />
          <br />
          <div className="xrva-inline">
            <button
              className={`xrva-btn ${isAudioLive ? "xrva-btn--primary" : ""}`}
              onClick={() =>
                xrvaSeekLive(xrvaAudioElRef.current, setIsAudioLive)
              }
            >
              Go to LIVE
            </button>
          </div>
        </section>

        {/* STATUS + LOG BOX (COLLAPSIBLE) */}
        <section className="xrva-card xrva-diagnostics">
          <div className="xrva-card__head">
            <h2 className="xrva-card__title">Diagnostics</h2>
            <div className="xrva-inline">
              <button
                className={`xrva-toggle ${xrvaShowStatus ? "is-on" : ""}`}
                onClick={() => setXrvaShowStatus((v) => !v)}
                aria-pressed={xrvaShowStatus}
                aria-label="Toggle Status visibility"
              >
                <span>Status</span>
              </button>
              <button
                className={`xrva-toggle ${xrvaShowLog ? "is-on" : ""}`}
                onClick={() => setXrvaShowLog((v) => !v)}
                aria-pressed={xrvaShowLog}
                aria-label="Toggle Log visibility"
              >
                <span>Log</span>
              </button>
            </div>
          </div>

          <div className="xrva-diagnostics__grid">
            {xrvaShowStatus && (
              <div className="xrva-card xrva-card--sub">
                {/*<h3 className="xrva-card__title xrva-card__title--sub">
                                    Status
                                </h3>*/}
                <button
                  className="xrva-btn xrva-btn--primary"
                  onClick={() => setXrvaStatus({})}
                >
                  Clear Status
                </button>
                <pre className="xrva-pre xrva-pre--mono">{xrvaStatusBlock}</pre>
              </div>
            )}
            {xrvaShowLog && (
              <div className="xrva-card xrva-card--sub">
                {/* <h3 className="xrva-card__title xrva-card__title--sub">
                                    Log{" "}
                                </h3> */}
                <button
                  className="xrva-btn xrva-btn--primary"
                  onClick={() => setXrvaLogLines([])}
                >
                  Clear Log
                </button>
                <pre className="xrva-pre xrva-pre--mono xrva-pre--scroll">
                  {xrvaLogBlock}
                </pre>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="xrva-footer">
        <p>&copy; DTG Testing</p>
      </footer>
    </div>
  );
}
